import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Image, Mic, Video, Sticker, FileText } from "lucide-react";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";
import MultimodalKpiPanel from "@/components/admin/MultimodalKpiPanel";

type Row = {
  id: string;
  kind: string;
  mimetype: string | null;
  description: string;
  transcript: string | null;
  caption: string | null;
  sender_name: string | null;
  created_at: string;
  group_id: string | null;
  confidence: number | null;
  model_used: string | null;
};

const KIND_META: Record<string, { icon: typeof Image; color: string; label: string }> = {
  image: { icon: Image, color: "bg-blue-500/10 text-blue-700", label: "Imagem" },
  audio: { icon: Mic, color: "bg-purple-500/10 text-purple-700", label: "Áudio" },
  video: { icon: Video, color: "bg-red-500/10 text-red-700", label: "Vídeo" },
  sticker: { icon: Sticker, color: "bg-amber-500/10 text-amber-700", label: "Figurinha" },
  document: { icon: FileText, color: "bg-emerald-500/10 text-emerald-700", label: "Documento" },
};

export default function MediaInsights() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("multimodal_descriptions")
        .select("id, kind, mimetype, description, transcript, caption, sender_name, created_at, group_id, confidence, model_used")
        .order("created_at", { ascending: false })
        .limit(100);
      if (cancelled) return;
      if (!error && data) {
        setRows(data as Row[]);
        const tally: Record<string, number> = {};
        data.forEach((r: any) => { tally[r.kind] = (tally[r.kind] ?? 0) + 1; });
        setCounts(tally);
      }
      setLoading(false);
    };
    load();
    const channel = supabase
      .channel("multimodal_descriptions_live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "multimodal_descriptions" }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, []);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <ShadowModeBanner context="Multimodal v19 está em shadow." compact />
      <div>
        <h1 className="text-3xl font-bold">Mídia recebida — descrições e transcrições</h1>
        <p className="text-muted-foreground mt-1">
          Tudo que a Syra "viu" e "ouviu" no WhatsApp nos últimos 14 dias.
        </p>
      </div>

      <MultimodalKpiPanel />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(KIND_META).map(([kind, meta]) => {
          const Icon = meta.icon;
          return (
            <Card key={kind}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-md ${meta.color}`}><Icon className="w-5 h-5" /></div>
                <div>
                  <div className="text-2xl font-bold">{counts[kind] ?? 0}</div>
                  <div className="text-xs text-muted-foreground">{meta.label}</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Últimas 100 descrições</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Nenhuma mídia processada ainda. Mande uma imagem, áudio ou figurinha em um grupo para ver aparecer aqui.
            </p>
          ) : (
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-3">
                {rows.map((row) => {
                  const meta = KIND_META[row.kind] ?? KIND_META.image;
                  const Icon = meta.icon;
                  return (
                    <div key={row.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${meta.color}`}><Icon className="w-4 h-4" /></div>
                          <Badge variant="outline">{meta.label}</Badge>
                          {row.sender_name && <span className="text-sm font-medium">{row.sender_name}</span>}
                          {row.confidence != null && (
                            <Badge variant="secondary" className="text-xs">conf {Number(row.confidence).toFixed(2)}</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(row.created_at).toLocaleString("pt-BR")}
                        </span>
                      </div>
                      {row.caption && (
                        <p className="text-sm italic text-muted-foreground">Legenda: "{row.caption}"</p>
                      )}
                      {row.transcript && (
                        <div className="text-sm">
                          <span className="font-medium">Transcrição: </span>
                          <span>{row.transcript}</span>
                        </div>
                      )}
                      {row.description && row.description !== row.transcript && (
                        <div className="text-sm">
                          <span className="font-medium">Descrição: </span>
                          <span>{row.description}</span>
                        </div>
                      )}
                      {row.model_used && (
                        <div className="text-xs text-muted-foreground">Modelo: {row.model_used}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
